import React, { useState, useEffect } from 'react';
import './EventForm.css';

const EventForm = ({ event, categories, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    start_datetime: '',
    end_datetime: '',
    timezone: 'UTC',
    location_type: 'physical',
    location_name: '',
    address: '',
    city: '',
    state: '',
    country: '',
    postal_code: '',
    latitude: 0.0,
    longitude: 0.0,
    location_url: '',
    organizer_id: 1, // Default to admin user
    category_id: null,
    status: 'draft',
    is_featured: false,
    is_public: true,
    max_attendees: null,
    registration_deadline: '',
    image_url: '',
    thumbnail_url: '',
    tags: ''
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (event) {
      setFormData({
        ...event,
        start_datetime: event.start_datetime ? formatDateTimeLocal(event.start_datetime) : '',
        end_datetime: event.end_datetime ? formatDateTimeLocal(event.end_datetime) : '',
        registration_deadline: event.registration_deadline ? formatDateTimeLocal(event.registration_deadline) : '',
      });
    }
  }, [event]);

  const formatDateTimeLocal = (isoString) => {
    const date = new Date(isoString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!formData.start_datetime) {
      newErrors.start_datetime = 'Start date/time is required';
    }

    if (!formData.end_datetime) {
      newErrors.end_datetime = 'End date/time is required';
    }

    if (formData.start_datetime && formData.end_datetime) {
      if (new Date(formData.end_datetime) <= new Date(formData.start_datetime)) {
        newErrors.end_datetime = 'End date/time must be after start date/time';
      }
    }

    if (formData.location_type === 'virtual' && !formData.location_url) {
      newErrors.location_url = 'Meeting link is required for virtual events';
    }

    if (formData.latitude && (formData.latitude < -90 || formData.latitude > 90)) {
      newErrors.latitude = 'Latitude must be between -90 and 90';
    }

    if (formData.longitude && (formData.longitude < -180 || formData.longitude > 180)) {
      newErrors.longitude = 'Longitude must be between -180 and 180';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    // Convert datetime-local to ISO 8601
    const submitData = {
      ...formData,
      start_datetime: new Date(formData.start_datetime).toISOString(),
      end_datetime: new Date(formData.end_datetime).toISOString(),
      registration_deadline: formData.registration_deadline ? new Date(formData.registration_deadline).toISOString() : null,
      latitude: formData.latitude !== null && formData.latitude !== '' ? parseFloat(formData.latitude) : 0.0,
      longitude: formData.longitude !== null && formData.longitude !== '' ? parseFloat(formData.longitude) : 0.0,
      max_attendees: formData.max_attendees ? parseInt(formData.max_attendees) : null,
      category_id: formData.category_id ? parseInt(formData.category_id) : null,
      organizer_id: 1, // Ensure organizer_id is always 1
    };

    onSave(submitData);
  };

  return (
    <div className="event-form-container">
      <div className="event-form-header">
        <h3>{event ? 'Edit Event' : 'Create New Event'}</h3>
        <button className="close-btn" onClick={onClose}>×</button>
      </div>

      <form onSubmit={handleSubmit} className="event-form">
        <div className="form-section">
          <h4>Basic Information</h4>
          
          <div className="form-group">
            <label>Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              className={errors.title ? 'error' : ''}
              placeholder="Enter event title"
            />
            {errors.title && <span className="error-message">{errors.title}</span>}
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              rows={4}
              placeholder="Enter event description"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Category</label>
              <select
                value={formData.category_id || ''}
                onChange={(e) => handleChange('category_id', e.target.value)}
              >
                <option value="">Select category</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Tags</label>
              <input
                type="text"
                value={formData.tags}
                onChange={(e) => handleChange('tags', e.target.value)}
                placeholder="technology,conference,networking"
              />
              <small>Comma-separated tags</small>
            </div>
          </div>
        </div>

        <div className="form-section">
          <h4>Date & Time</h4>
          
          <div className="form-row">
            <div className="form-group">
              <label>Start Date & Time *</label>
              <input
                type="datetime-local"
                value={formData.start_datetime}
                onChange={(e) => handleChange('start_datetime', e.target.value)}
                className={errors.start_datetime ? 'error' : ''}
              />
              {errors.start_datetime && <span className="error-message">{errors.start_datetime}</span>}
            </div>

            <div className="form-group">
              <label>End Date & Time *</label>
              <input
                type="datetime-local"
                value={formData.end_datetime}
                onChange={(e) => handleChange('end_datetime', e.target.value)}
                className={errors.end_datetime ? 'error' : ''}
              />
              {errors.end_datetime && <span className="error-message">{errors.end_datetime}</span>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Timezone</label>
              <select
                value={formData.timezone}
                onChange={(e) => handleChange('timezone', e.target.value)}
              >
                <option value="UTC">UTC</option>
                <option value="America/New_York">Eastern Time (US)</option>
                <option value="America/Chicago">Central Time (US)</option>
                <option value="America/Denver">Mountain Time (US)</option>
                <option value="America/Los_Angeles">Pacific Time (US)</option>
                <option value="Europe/London">London</option>
                <option value="Europe/Paris">Paris</option>
                <option value="Asia/Kolkata">India</option>
                <option value="Asia/Tokyo">Tokyo</option>
                <option value="Australia/Sydney">Sydney</option>
              </select>
            </div>

            <div className="form-group">
              <label>Registration Deadline</label>
              <input
                type="datetime-local"
                value={formData.registration_deadline}
                onChange={(e) => handleChange('registration_deadline', e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="form-section">
          <h4>Location</h4>
          
          <div className="form-group">
            <label>Location Type *</label>
            <div className="radio-group">
              <label>
                <input
                  type="radio"
                  value="physical"
                  checked={formData.location_type === 'physical'}
                  onChange={(e) => handleChange('location_type', e.target.value)}
                />
                Physical
              </label>
              <label>
                <input
                  type="radio"
                  value="virtual"
                  checked={formData.location_type === 'virtual'}
                  onChange={(e) => handleChange('location_type', e.target.value)}
                />
                Virtual
              </label>
            </div>
          </div>

          {formData.location_type === 'physical' ? (
            <>
              <div className="form-group">
                <label>Venue Name</label>
                <input
                  type="text"
                  value={formData.location_name}
                  onChange={(e) => handleChange('location_name', e.target.value)}
                  placeholder="Convention Center, Hotel, etc."
                />
              </div>

              <div className="form-group">
                <label>Address</label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => handleChange('address', e.target.value)}
                  placeholder="Street address"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>City</label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => handleChange('city', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label>State/Province</label>
                  <input
                    type="text"
                    value={formData.state}
                    onChange={(e) => handleChange('state', e.target.value)}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Country</label>
                  <input
                    type="text"
                    value={formData.country}
                    onChange={(e) => handleChange('country', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label>Postal Code</label>
                  <input
                    type="text"
                    value={formData.postal_code}
                    onChange={(e) => handleChange('postal_code', e.target.value)}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Latitude</label>
                  <input
                    type="number"
                    step="0.00000001"
                    value={formData.latitude || ''}
                    onChange={(e) => handleChange('latitude', e.target.value)}
                    placeholder="40.7128"
                    className={errors.latitude ? 'error' : ''}
                  />
                  {errors.latitude && <span className="error-message">{errors.latitude}</span>}
                </div>

                <div className="form-group">
                  <label>Longitude</label>
                  <input
                    type="number"
                    step="0.00000001"
                    value={formData.longitude || ''}
                    onChange={(e) => handleChange('longitude', e.target.value)}
                    placeholder="-74.0060"
                    className={errors.longitude ? 'error' : ''}
                  />
                  {errors.longitude && <span className="error-message">{errors.longitude}</span>}
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="form-group">
                <label>Platform Name</label>
                <input
                  type="text"
                  value={formData.location_name}
                  onChange={(e) => handleChange('location_name', e.target.value)}
                  placeholder="Zoom, Microsoft Teams, Google Meet, etc."
                />
              </div>

              <div className="form-group">
                <label>Meeting Link *</label>
                <input
                  type="url"
                  value={formData.location_url}
                  onChange={(e) => handleChange('location_url', e.target.value)}
                  placeholder="https://zoom.us/j/123456789"
                  className={errors.location_url ? 'error' : ''}
                />
                {errors.location_url && <span className="error-message">{errors.location_url}</span>}
              </div>
            </>
          )}
        </div>

        <div className="form-section">
          <h4>Event Settings</h4>
          
          <div className="form-row">
            <div className="form-group">
              <label>Status</label>
              <select
                value={formData.status}
                onChange={(e) => handleChange('status', e.target.value)}
              >
                <option value="draft">Draft</option>
                <option value="published">Published</option>
                <option value="cancelled">Cancelled</option>
                <option value="completed">Completed</option>
              </select>
            </div>

            <div className="form-group">
              <label>Max Attendees</label>
              <input
                type="number"
                value={formData.max_attendees || ''}
                onChange={(e) => handleChange('max_attendees', e.target.value)}
                placeholder="Leave empty for unlimited"
                min="1"
              />
            </div>
          </div>

          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                checked={formData.is_public}
                onChange={(e) => handleChange('is_public', e.target.checked)}
              />
              Public Event
            </label>
            <small>Public events are visible to everyone</small>
          </div>

          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                checked={formData.is_featured}
                onChange={(e) => handleChange('is_featured', e.target.checked)}
              />
              Featured Event
            </label>
            <small>Featured events appear prominently on the homepage</small>
          </div>
        </div>

        <div className="form-section">
          <h4>Media</h4>
          
          <div className="form-group">
            <label>Event Image URL</label>
            <input
              type="url"
              value={formData.image_url}
              onChange={(e) => handleChange('image_url', e.target.value)}
              placeholder="https://example.com/event-banner.jpg"
            />
          </div>

          <div className="form-group">
            <label>Thumbnail URL</label>
            <input
              type="url"
              value={formData.thumbnail_url}
              onChange={(e) => handleChange('thumbnail_url', e.target.value)}
              placeholder="https://example.com/event-thumb.jpg"
            />
          </div>
        </div>

        <div className="form-actions">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary">
            {event ? 'Update Event' : 'Create Event'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EventForm;
